//@ts-ignore
import { useTheme } from 'styled-components'
import { AnalyticUserType, useAnalytics } from 'hooks/use-analytics/useAnalytics'
import { Dispatch, FunctionComponent, SetStateAction, useState } from 'react'
import { BackendUser, CommentPost, Content, JobProps, PostTypes, ReportActionTypes } from 'common/types'
import { ConfirmModal } from 'components/core/modal'
import { useAuth } from 'pages/auth/hooks/useAuth'
import { useLocalStorage } from 'hooks/use-local-storage/useLocalStorage'
import { ReportComponent } from '../../reportComponent/ReportComponent'

interface ReportCheckboxListProps {
  contentId?: string
  setShowReportModal: Function
  type: string
  reportContent?: Function
  user: BackendUser
  post?: Content
  setPosts?: Dispatch<SetStateAction<any>>
  comment?: CommentPost
  setComments?: Function
  posts?: Content[]
  comments?: CommentPost[]
  getPost?: Function
  notify?: Function
  open: boolean
  maxChars: number
  handleReport: Function
  job?: JobProps
  preConfirm?: boolean
}

export const ReportCheckboxList: FunctionComponent<ReportCheckboxListProps> = ({
  setShowReportModal,
  type,
  post,
  setPosts,
  comment,
  setComments,
  posts,
  comments,
  open,
  handleReport,
  job,
  preConfirm
}) => {
  const { backendUser, backendPartner } = useAuth()
  const { handleTracker } = useAnalytics()
  const { getItem, setItem } = useLocalStorage()
  const [reason, setReason] = useState<string>()
  const [reasonDescription, setReasonDescription] = useState<string>('')
  const [showConfirm, setShowConfirm] = useState(false)

  const isPost =
    type === PostTypes.ARTICLE ||
    (!comment && type === PostTypes.ACTIVITY && post?.content[1]?.type === PostTypes.ARTICLE)
  const isComment = type === PostTypes.ACTIVITY
  const isJob = type === ReportActionTypes.JOB

  const reportType =
    (isPost && ReportActionTypes.POST) || (isComment && ReportActionTypes.COMMENT) || (isJob && ReportActionTypes.JOB)

  const onClose = () => {
    setShowReportModal(false)
    setReason('')
    setReasonDescription('')
  }

  const onConfirm = async () => {
    const reportResponse = await handleReport(
      type,
      post,
      comment,
      job,
      setReason,
      setReasonDescription,
      reasonDescription,
      reason
    )

    if (!reportResponse) {
      if (isPost) {
        const id = post?.id
        const newPosts = [...posts]
        const index = newPosts?.findIndex(post => post?.id === id)
        newPosts[index].reportedBy = [backendUser?.id]

        const logData = {
          'User ID': backendUser?.id,
          'Account Type': backendUser?.partnerId ? 'Partner' : backendUser?.role,
          'Partner ID': backendPartner?.id,
          'Company Name': backendPartner?.name,
          'Report category': reason,
          'Post ID': id
        }
        await handleTracker(AnalyticUserType.ALL, 'Post reported', logData, 'mixpanel', null, backendUser)

        setPosts([...newPosts])
      } else if (isComment) {
        const id = comment?.id

        const index = comments?.findIndex(comment => comment.id === id)
        comments.splice(index, 1, {
          ...comment,
          reportedBy: [backendUser?.id]
        })

        const logData = {
          'User ID': backendUser?.id,
          'Account Type': backendUser?.partnerId ? 'Partner' : backendUser?.role,
          'Partner ID': backendPartner?.id,
          'Company Name': backendPartner?.name,
          'Report category': reason,
          'Comment ID': id
        }
        await handleTracker(AnalyticUserType.ALL, 'Comment reported', logData, 'mixpanel', null, backendUser)

        setComments([...comments])
      } else if (isJob) {
        const reportedJobsJSON = getItem('reportedJobs')
        let reportedJobs = []

        try {
          reportedJobs = reportedJobsJSON ? JSON.parse(reportedJobsJSON) : []
        } catch (err) { }

        const newReportedJobs = [...reportedJobs, job?.id]

        const logData = {
          'User ID': backendUser?.id,
          'Account Type': backendUser?.partnerId ? 'Partner' : backendUser?.role,
          'Partner ID': backendPartner?.id,
          'Company Name': backendPartner?.name,
          'Job ID': job?.id,
          'Report category': reason
        }
        await handleTracker(AnalyticUserType.ALL, 'Job reported', logData, 'mixpanel', null, backendUser)

        setItem('reportedJobs', JSON.stringify(newReportedJobs))
      }
    }

    onClose()
  }

  return (
    <>
      <ConfirmModal
        closeAfterTransition={false}
        onClose={isFromConfirm => {
          isFromConfirm && !preConfirm && onClose()

          if (!isFromConfirm) {
            onClose()
          }
        }}
        open={open}
        title={''}
        fullWidth
        onConfirm={() => {
          preConfirm ? setShowConfirm(true) : onConfirm()
        }}
        cancelText={'Cancel'}
        disabled={!reason || reason === 'Select a category'}
        confirmText={'Submit'}
        maxWidth="sm"
        isReportingContent
      >
        <ReportComponent type={type} setReason={setReason} reason={reason} setReasonDescription={setReasonDescription} />
      </ConfirmModal>
      {preConfirm && showConfirm && (
        <ConfirmModal
          title=""
          message={`Are you sure you want to report this ${reportType}?`}
          open={showConfirm}
          onClose={() => setShowConfirm(false)}
          onConfirm={async () => {
            await onConfirm()
            setShowConfirm(false)
          }}
        />
      )}
    </>
  )
}
